import { useTranslations } from "use-intl";

import { Link } from "@/components/link";
import { formatCatalogTaxonomyLabel } from "@/lib/music-taxonomy";
import { cn } from "@/lib/utils";
import type { TaxonomyPageLink } from "@garimpa/api/music/types";

interface GenreStyleTagsProps {
  genres?: string[] | null;
  styles?: string[] | null;
  /** Published editorial pages for these keys; matching chips link there instead of search. */
  pages?: readonly TaxonomyPageLink[] | null;
  className?: string;
  tagClassName?: string;
}

/**
 * Renders genre and style hashtag links for album and release pages.
 * Returns null when both arrays are empty or missing.
 * A chip with a published editorial page links there as a followed link; the rest target noindex
 * search results and are `nofollow` to keep crawl budget on indexable pages.
 */
export function GenreStyleTags({
  genres,
  styles,
  pages,
  className,
  tagClassName,
}: GenreStyleTagsProps) {
  const tg = useTranslations("shared.genres");
  const ts = useTranslations("shared.styles");

  const formatGenre = (value: string) => {
    const key = value as Parameters<typeof tg>[0];
    return tg.has(key) ? tg(key) : formatCatalogTaxonomyLabel(value);
  };

  const formatStyle = (value: string) => {
    const key = value as Parameters<typeof ts>[0];
    return ts.has(key) ? ts(key) : formatCatalogTaxonomyLabel(value);
  };

  if (!genres?.length && !styles?.length) return null;

  const chipClassName = cn(
    "text-sm text-muted-foreground hover:text-primary transition-colors",
    tagClassName,
  );
  const pagePath = (family: TaxonomyPageLink["family"], key: string) =>
    pages?.find((page) => page.family === family && page.subjectKey === key)?.path;

  const renderChip = (family: TaxonomyPageLink["family"], key: string, label: string) => {
    const path = pagePath(family, key);
    return path ? (
      <Link key={`${family}-${key}`} to={path} className={chipClassName}>
        #{label}
      </Link>
    ) : (
      <Link
        key={`${family}-${key}`}
        to="/search"
        search={family === "genre" ? { genre: key } : { style: key }}
        rel="nofollow"
        className={chipClassName}
      >
        #{label}
      </Link>
    );
  };

  return (
    <div className={cn("flex flex-wrap gap-x-3 gap-y-1 mb-6", className)}>
      {genres?.map((genre) => renderChip("genre", genre, formatGenre(genre)))}
      {styles?.map((style) => renderChip("style", style, formatStyle(style)))}
    </div>
  );
}
